import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RateReviewIcon from "@mui/icons-material/RateReview";
import CloseIcon from "@mui/icons-material/Close";
import { HandymanAppState } from "../../configuration/redux/reducers";
import { getOrderReviews, createReview } from "./actions";
import { getOrderDetails } from "../Orders/actions";
import {
  CreateReviewCommand,
  ReviewType,
  CustomerOrderStatus,
  HandymanOrderStatus,
} from '../../configuration/api/apiSchema';
import ReviewsList from "../../components/Reviews/ReviewsList";
import CreateReviewForm from "../../components/Reviews/CreateReviewForm";

const OrderReviewsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showCreateReview, setShowCreateReview] = useState(false);

  const currentUser = useSelector((state: HandymanAppState) => state.user.currentUserDetails);
  const { orderReviews, orderReviewsLoading, orderReviewsError, orderReviewsTotalCount } = useSelector(
    (state: HandymanAppState) => state.review
  );
  const { orderDetails } = useSelector((state: HandymanAppState) => state.order);
  const currentPage = useSelector((state: HandymanAppState) => state.review.currentPage);

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderReviews(orderId, currentPage) as any);
      dispatch(getOrderDetails(orderId) as any);
    }
  }, [dispatch, orderId, currentPage]);

  const handlePageChange = (page: number) => {
    if (orderId) {
      dispatch(getOrderReviews(orderId, page) as any);
    }
  };

  const canLeaveReview = () => {
    if (!currentUser?.id || !orderDetails) return false;
    
    // Only completed orders can be reviewed
    if (orderDetails.customerStatus !== CustomerOrderStatus.Completed)
      return false;
    
    // Check if user is part of this order
    const isCustomer = currentUser.id === orderDetails.customer?.id;
    const isHandyman = currentUser.id === orderDetails.handyman?.id;
    
    if (!isCustomer && !isHandyman) return false;
    
    // Check if user has already left a review
    const hasReviewed = orderReviews.some(review => 
      review.reviewer?.id === currentUser.id
    );
    
    return !hasReviewed;
  };

  const getRevieweeId = () => {
    if (!currentUser?.id || !orderDetails) return "";
    
    const isCustomer = currentUser.id === orderDetails.customer?.id;
    return isCustomer 
      ? orderDetails.handyman?.id || ""
      : orderDetails.customer?.id || "";
  };

  const getReviewType = () => {
    if (!currentUser?.id || !orderDetails) return ReviewType.CustomerToHandyman;
    
    const isCustomer = currentUser.id === orderDetails.customer?.id;
    return isCustomer 
      ? ReviewType.CustomerToHandyman 
      : ReviewType.HandymanToCustomer;
  };

  const handleSubmitReview = async (data: CreateReviewCommand) => {
    await dispatch(createReview(data) as any);
    setShowCreateReview(false);
    // Refresh reviews
    if (orderId) {
      dispatch(getOrderReviews(orderId, currentPage) as any);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Order
        </Button>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" gutterBottom>
            Order Reviews
          </Typography>
          {canLeaveReview() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RateReviewIcon />}
              onClick={() => setShowCreateReview(true)}
            >
              Write a Review
            </Button>
          )}
        </Box>
        {orderDetails && (
          <Typography variant="subtitle1" color="text.secondary">
            Order: {orderDetails.name || "Order #" + orderId?.substring(0, 8)}
          </Typography>
        )}
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <ReviewsList
          reviews={orderReviews}
          loading={orderReviewsLoading}
          error={orderReviewsError}
          totalCount={orderReviewsTotalCount}
          onPageChange={handlePageChange}
          showActions={false}
          emptyMessage="No reviews for this order yet"
          title=""
          currentPage={currentPage}
        />
      </Paper>

      {/* Create Review Dialog */}
      <Dialog
        open={showCreateReview}
        onClose={() => setShowCreateReview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Write a Review</Typography>
            <IconButton onClick={() => setShowCreateReview(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <CreateReviewForm
            orderId={orderId || ""}
            reviewerId={currentUser?.id || ""}
            revieweeId={getRevieweeId()}
            reviewType={getReviewType()}
            onSubmit={handleSubmitReview}
            onCancel={() => setShowCreateReview(false)}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default OrderReviewsPage; 